/**
 * @file `TerminalSub` — `SubscriptionContract` for the lab terminal
 *       proxy WS. The single source of truth shared between the
 *       lab-side proxy handler (which binds it via `bindWebSocket`)
 *       and the SPA's `Terminal` Client (which dials it via
 *       `WsClientTransport.subscribe`).
 * @module @labcraft/lab/shared/terminal-subscription
 *
 * Lab is a proxy in front of the hypervisor's pty bridge — same
 * control + binary protocol on the browser-facing side, plus a few
 * extra server-pushed envelopes (`role`, `present`, `chapter`) that
 * lab synthesizes on top of the upstream transport. The hypervisor's
 * symmetric contract lives in `@labcraft/hypervisor`'s
 * `shared/subscriptions.ts` under `PtySub`; the frames lab forwards
 * verbatim (`resize`, `resume`, `lifecycle`) are **imported from
 * there** (umbrella #1048 / #1050) — lab already depends on
 * `@labcraft/hypervisor` at runtime, so the pre-#1050 "keep the
 * dependency runtime-only" rationale for hand-mirroring was moot, and
 * three drift-prone copies of the lifecycle enum existed to serve it.
 * Only lab's genuine superset frames (`role`, `command`,
 * `budget-exhausted`, `focus`, `mentor`, `mentor-pending`, `probe`,
 * `probe-result`, `wrong-turn`, `recovered`) are declared here.
 *
 * Frame channel split:
 *   - **Binary** (pty I/O bytes) flows through `sendBinary` /
 *     `onBinary` — NOT validated against any schema; the bytes are
 *     opaque.
 *   - **Text JSON** flows through `send` / `onData`:
 *     - Client → Server: `resize`, `resume` — control envelopes the
 *       lab proxy forwards to the hypervisor verbatim. The hypervisor's
 *       `PtyControlFrameSchema` validates them at the upstream hop;
 *       lab re-validates here so a stale SPA build can't bypass the
 *       contract by speaking direct-to-hypervisor shape over the lab
 *       wire.
 *     - Server → Client: `role`, `present`, `chapter` — synthesized
 *       by lab. `role` (driver vs observer) and `present` (live
 *       attachment count) per ADR-006 (e/f); `chapter`
 *       (account-global pointer) per ADR-006 (g).
 */

import { z } from 'zod'
import { defineSubscription, CoreString } from '@labcraft/core'
import { PtyResumeFrameSchema } from '@labcraft/hypervisor/shared/subscriptions'
import {
  AtCapacityFrameSchema,
  BudgetExhaustedFrameSchema,
  CommandFrameSchema,
  EngagementFrameSchema,
  FocusFrameSchema,
  LifecycleFrameSchema,
  MentorFrameSchema,
  MentorPendingFrameSchema,
  ProbeFrameSchema,
  ProbeResultFrameSchema,
  RecoveredFrameSchema,
  ResizeFrameSchema,
  RoleFrameSchema,
  WrongTurnFrameSchema,
} from './ws_messages.ts'

////////////////////////////////////////////////////////////////////////////////
// Control envelopes (client → server)
////////////////////////////////////////////////////////////////////////////////

/**
 * Resume envelope sent by `WsSubscription` on every reconnect after
 * the first to ask the server to re-emit current state. The
 * hypervisor's own `PtyResumeFrameSchema` — the lab proxy forwards it
 * verbatim, so both hops validate against one schema.
 */
export const ResumeFrameSchema = PtyResumeFrameSchema

/** Parsed `resume` envelope. */
export type ResumeFrame = z.infer<typeof ResumeFrameSchema>

/**
 * Discriminated union over every text-frame variant the browser sends
 * on this WS. `resize` / `resume` are the hypervisor's own
 * control-frame schemas (minus `open` / `suspend`, which lab itself
 * originates on the upstream hop) and forward verbatim; `focus`
 * (#1148), `probe` (#1149), and `wrong-turn` (#1150) are lab's own —
 * `focus` arms the mentor session's context, `probe` dispatches a
 * state check to the hypervisor's HTTP probe route, `wrong-turn`
 * reports a Tier-1 firing (captured lab-side + relayed to the mentor),
 * `recovered` (#1566) reports an objective satisfied after a struggle
 * (lab marks capture RECOVERED + does the gated encouragement relay);
 * all terminate at lab, never crossing the pty WS to the hypervisor.
 */
export const TerminalClientFrameSchema = z.discriminatedUnion('type', [
  ResizeFrameSchema,
  ResumeFrameSchema,
  FocusFrameSchema,
  ProbeFrameSchema,
  WrongTurnFrameSchema,
  RecoveredFrameSchema,
])

/** Parsed text-frame the browser sends. */
export type TerminalClientFrame = z.infer<typeof TerminalClientFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// Server-push envelopes (server → client)
////////////////////////////////////////////////////////////////////////////////

/**
 * Discriminated union over every text-frame variant lab pushes to the
 * browser. `role` is the multi-tab driver/observer arbitration signal
 * (consumer: the demote-toast in `TerminalPane`) — a planned surface
 * kept ahead of its producer. #1060 retired the `present` + `chapter`
 * envelopes (declared, never produced, no live consumer). `mentor`
 * (#1148 / #1566) is the mentor → lab → SPA disposition relay (the former
 * nudge-only frame, generalized to carry every disposition); `mentor-pending`
 * (#1566) is the lab-owned "AI looking…" beat. `probe-result` (#1149)
 * answers the client `probe` frame with its satisfied verdict.
 * `at-capacity` (#1257 Part E) is sent just before the WS closes when the
 * host is full and the learner is queued — it carries their place in line +
 * whether push notifications are available for the enable-notifications
 * opt-in.
 */
export const TerminalServerFrameSchema = z.discriminatedUnion('type', [
  RoleFrameSchema,
  LifecycleFrameSchema,
  EngagementFrameSchema,
  CommandFrameSchema,
  BudgetExhaustedFrameSchema,
  MentorFrameSchema,
  MentorPendingFrameSchema,
  ProbeResultFrameSchema,
  AtCapacityFrameSchema,
])

/** Parsed text-frame lab pushes to the browser. */
export type TerminalServerFrame = z.infer<typeof TerminalServerFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// Path params
////////////////////////////////////////////////////////////////////////////////

/**
 * Path params for the lab terminal WS. The browser names the machine
 * by the recipe's author-chosen `machineId` (`shell`, `client`, …);
 * lab resolves to a content-keyed internal vmId before opening the
 * upstream WS to the hypervisor.
 *
 * @property machineId - Author-named machine slot from the chapter
 *   recipe.
 * @property terminalId - Author-named terminal slot within the
 *   machine. v1 supports a single serial-console pty per VM; any
 *   non-empty slug resolves to it.
 */
export const TerminalSubParamsSchema = z.object({
  machineId: z.string().min(1),
  terminalId: z.string().min(1),
})

/** Parsed path params. */
export type TerminalSubParams = z.infer<typeof TerminalSubParamsSchema>

////////////////////////////////////////////////////////////////////////////////
// TerminalSub contract
////////////////////////////////////////////////////////////////////////////////

/**
 * `WS /api/machines/:machineId/terminals/:terminalId` — lab's
 * browser-facing terminal proxy. The SPA's `Terminal` Client dials
 * this via `ws.subscribe(TerminalSub, { params: { machineId,
 * terminalId } })`; the lab service binds the same contract via
 * `bindWebSocket(TerminalSub, handler)` and validates inbound
 * `clientFrame` text frames before forwarding to the hypervisor.
 *
 * Binary frames pass through both directions unvalidated.
 */
export const TerminalSub = defineSubscription({
  path: '/api/machines/:machineId/terminals/:terminalId',
  params: TerminalSubParamsSchema,
  clientFrame: TerminalClientFrameSchema,
  serverFrame: TerminalServerFrameSchema,
  description: CoreString.oneLine`
    Per-terminal WS proxy. Browser dials lab; lab proxies to the hypervisor
    over WG (or loopback in dev). Binary frames are pty bytes (opaque,
    unvalidated). Text frames are typed control / status envelopes.
  `,
})
